import { createPermission } from '@backstage/plugin-permission-common';

/**
 * Resource type identifier for Pulse check entities, used to scope
 * resource-specific permission rules.
 *
 * @public
 */
export const PULSE_CHECK_RESOURCE_TYPE = 'pulse-check';

/**
 * Resource type identifier for Pulse check result entities, used to scope
 * sensitive fact read permissions to a specific check/entity pair.
 *
 * @public
 */
export const PULSE_CHECK_RESULT_RESOURCE_TYPE = 'pulse-check-result';

/**
 * Resource type identifier for Pulse check exemption entities, used to scope
 * exemption update permissions independently from manual check-result updates.
 *
 * @public
 */
export const PULSE_CHECK_EXEMPTION_RESOURCE_TYPE = 'pulse-check-exemption';

/**
 * Resource type identifier for Pulse check *definitions*, used to scope
 * ownership-based create/update/delete rules independently from the
 * `pulse-check` (refresh/result) resource type.
 *
 * @public
 */
export const PULSE_CHECK_DEFINITION_RESOURCE_TYPE = 'pulse-check-definition';

/** Resource type identifier for UI-managed Pulse track definitions. */
export const PULSE_TRACK_DEFINITION_RESOURCE_TYPE = 'pulse-track-definition';

/** Resource type identifier for UI-managed Pulse fact collector definitions. */
export const PULSE_FACT_COLLECTOR_DEFINITION_RESOURCE_TYPE =
  'pulse-fact-collector-definition';

/**
 * Separator used in composite Pulse resource references.
 *
 * @public
 */
export const PULSE_RESOURCE_REF_SEPARATOR = '|';

/**
 * Parsed parts of a Pulse check refresh resource reference.
 *
 * @public
 */
export type PulseCheckResourceRefParts = {
  checkId: string;
  entityRef: string;
};

/**
 * Builds a composite resource reference used by `pulse.check.refresh`.
 *
 * Format: `checkId|entityRef`
 *
 * @public
 */
export const toPulseCheckResourceRef = (
  checkId: string,
  entityRef: string,
): string => {
  return [encodeURIComponent(checkId), encodeURIComponent(entityRef)].join(
    PULSE_RESOURCE_REF_SEPARATOR,
  );
};

/**
 * Builds a resource reference for a Pulse check *definition* (id only), used by
 * the create/update/delete permissions scoped to
 * {@link PULSE_CHECK_DEFINITION_RESOURCE_TYPE}. Distinct from the
 * `checkId|entityRef` refresh ref: a bare, separator-free id denotes the
 * definition itself.
 *
 * @public
 */
export const toPulseCheckDefinitionResourceRef = (checkId: string): string =>
  checkId;

/**
 * Parses a `pulse.check.refresh` composite resource reference.
 *
 * @public
 */
export const parsePulseCheckResourceRef = (
  resourceRef: string,
): PulseCheckResourceRefParts | undefined => {
  const parts = resourceRef.split(PULSE_RESOURCE_REF_SEPARATOR);
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return undefined;
  }

  try {
    return {
      checkId: decodeURIComponent(parts[0]),
      entityRef: decodeURIComponent(parts[1]),
    };
  } catch {
    return undefined;
  }
};

/** Builds a resource reference for a Pulse track definition. */
export const toPulseTrackDefinitionResourceRef = (trackId: string): string =>
  trackId;

/**
 * Builds a resource reference for a Pulse fact collector definition. Fact
 * names are only unique per integration, so the ref is `integrationId|factName`.
 */
export const toPulseFactCollectorDefinitionResourceRef = (
  integrationId: string,
  factName: string,
): string =>
  [encodeURIComponent(integrationId), encodeURIComponent(factName)].join(
    PULSE_RESOURCE_REF_SEPARATOR,
  );

/**
 * Grants full administrative access to Pulse, including managing checks,
 * scorecards, and configuration. Intended for platform owners and tooling teams.
 *
 * @public
 */
export const pulseAdminPermission = createPermission({
  name: 'pulse.admin',
  attributes: {
    action: 'read',
  },
});

/**
 * Grants read access to Pulse check definitions.
 *
 * @public
 */
export const pulseCheckReadPermission = createPermission({
  name: 'pulse.check.read',
  attributes: {
    action: 'read',
  },
});

/**
 * Grants refresh access to a specific Pulse check resource.
 * This is a resource permission scoped to {@link PULSE_CHECK_RESOURCE_TYPE}.
 * Also used to gate triggering a refresh (re-evaluation) of a check for an
 * entity, since this is semantically an update of the check's result. Access
 * is typically granted to the entity owner, the check owner, or Pulse
 * administrators.
 *
 * @public
 */
export const pulseCheckRefreshPermission = createPermission({
  name: 'pulse.check.refresh',
  attributes: {
    action: 'update',
  },
  resourceType: PULSE_CHECK_RESOURCE_TYPE,
});

/**
 * Grants update access to a manual check result for a specific entity.
 * This is scoped to the checked entity so ownership rules can restrict writes
 * to entity owners while allowing administrators through an explicit grant.
 *
 * @public
 */
export const pulseCheckManualUpdatePermission = createPermission({
  name: 'pulse.check.manual.update',
  attributes: {
    action: 'update',
  },
  resourceType: PULSE_CHECK_RESULT_RESOURCE_TYPE,
});

/**
 * Grants update access to an automated check exemption for a specific entity.
 * This is scoped to the checked entity so ownership rules can restrict writes
 * to entity owners while allowing administrators through an explicit grant.
 *
 * @public
 */
export const pulseCheckExemptionUpdatePermission = createPermission({
  name: 'pulse.check.exemption.update',
  attributes: {
    action: 'update',
  },
  resourceType: PULSE_CHECK_EXEMPTION_RESOURCE_TYPE,
});

/**
 * Grants permission to create new Pulse check definitions. Not resource-scoped;
 * typically granted to the `pulse-contributors` role.
 *
 * @public
 */
export const pulseCheckCreatePermission = createPermission({
  name: 'pulse.check.create',
  attributes: {
    action: 'create',
  },
});

/**
 * Grants permission to update an existing Pulse check definition.
 * Resource-scoped to {@link PULSE_CHECK_DEFINITION_RESOURCE_TYPE}; typically
 * granted to the check owner (via the IS_CHECK_OWNER conditional rule) or Pulse
 * administrators.
 *
 * @public
 */
export const pulseCheckUpdatePermission = createPermission({
  name: 'pulse.check.update',
  attributes: {
    action: 'update',
  },
  resourceType: PULSE_CHECK_DEFINITION_RESOURCE_TYPE,
});

/**
 * Grants permission to delete an existing Pulse check definition.
 * Resource-scoped to {@link PULSE_CHECK_DEFINITION_RESOURCE_TYPE}; typically
 * granted to the check owner (via the IS_CHECK_OWNER conditional rule) or Pulse
 * administrators.
 *
 * @public
 */
export const pulseCheckDeletePermission = createPermission({
  name: 'pulse.check.delete',
  attributes: {
    action: 'delete',
  },
  resourceType: PULSE_CHECK_DEFINITION_RESOURCE_TYPE,
});

/**
 * Grants read access to Pulse fact collector definitions.
 *
 * @public
 */
export const pulseFactCollectorReadPermission = createPermission({
  name: 'pulse.fact-collector.read',
  attributes: {
    action: 'read',
  },
});

/** Grants permission to create a UI-managed Pulse fact collector definition. */
export const pulseFactCollectorCreatePermission = createPermission({
  name: 'pulse.fact-collector.create',
  attributes: { action: 'create' },
});

/** Grants permission to update a UI-managed Pulse fact collector definition. */
export const pulseFactCollectorUpdatePermission = createPermission({
  name: 'pulse.fact-collector.update',
  attributes: { action: 'update' },
  resourceType: PULSE_FACT_COLLECTOR_DEFINITION_RESOURCE_TYPE,
});

/** Grants permission to delete a UI-managed Pulse fact collector definition. */
export const pulseFactCollectorDeletePermission = createPermission({
  name: 'pulse.fact-collector.delete',
  attributes: { action: 'delete' },
  resourceType: PULSE_FACT_COLLECTOR_DEFINITION_RESOURCE_TYPE,
});

/**
 * Grants read access to sensitive Pulse fact values.
 *
 * @public
 */
export const pulseFactSensitiveReadPermission = createPermission({
  name: 'pulse.fact.sensitive.read',
  attributes: {
    action: 'read',
  },
});

/**
 * Grants owner-scoped read access to sensitive Pulse fact values for a
 * specific checked entity. Evaluated via the IS_CHECKED_ENTITY_OWNER
 * conditional rule using the caller's full ownership entity refs.
 * Use `$ownerRefs` (not `$currentUser`) in the RBAC conditional policy.
 *
 * @public
 */
export const pulseFactSensitiveOwnerReadPermission = createPermission({
  name: 'pulse.fact.sensitive.owner.read',
  attributes: {
    action: 'read',
  },
  resourceType: PULSE_CHECK_RESULT_RESOURCE_TYPE,
});

/**
 * Grants read access to Pulse track definitions and results.
 *
 * @public
 */
export const pulseTrackReadPermission = createPermission({
  name: 'pulse.track.read',
  attributes: {
    action: 'read',
  },
});

/** Grants permission to create a UI-managed Pulse track definition. */
export const pulseTrackCreatePermission = createPermission({
  name: 'pulse.track.create',
  attributes: { action: 'create' },
});

/** Grants permission to update a UI-managed Pulse track definition. */
export const pulseTrackUpdatePermission = createPermission({
  name: 'pulse.track.update',
  attributes: { action: 'update' },
  resourceType: PULSE_TRACK_DEFINITION_RESOURCE_TYPE,
});

/** Grants permission to delete a UI-managed Pulse track definition. */
export const pulseTrackDeletePermission = createPermission({
  name: 'pulse.track.delete',
  attributes: { action: 'delete' },
  resourceType: PULSE_TRACK_DEFINITION_RESOURCE_TYPE,
});

/** Grants permission to recertify a specific Pulse track definition. */
export const pulseTrackRecertifyPermission = createPermission({
  name: 'pulse.track.recertify',
  attributes: { action: 'update' },
  resourceType: PULSE_TRACK_DEFINITION_RESOURCE_TYPE,
});

/**
 * All Pulse permissions, used for registration with the permission backend.
 *
 * @public
 */
export const pulsePermissions = [
  pulseAdminPermission,
  pulseCheckReadPermission,
  pulseCheckRefreshPermission,
  pulseCheckManualUpdatePermission,
  pulseCheckExemptionUpdatePermission,
  pulseCheckCreatePermission,
  pulseCheckUpdatePermission,
  pulseCheckDeletePermission,
  pulseFactCollectorReadPermission,
  pulseFactCollectorCreatePermission,
  pulseFactCollectorUpdatePermission,
  pulseFactCollectorDeletePermission,
  pulseFactSensitiveReadPermission,
  pulseFactSensitiveOwnerReadPermission,
  pulseTrackReadPermission,
  pulseTrackCreatePermission,
  pulseTrackUpdatePermission,
  pulseTrackDeletePermission,
  pulseTrackRecertifyPermission,
];
